import React, { useRef, useState, useEffect } from "react";
import { motion, useTransform, AnimatePresence, useMotionTemplate, useMotionValue } from "framer-motion";
import { Calendar, Users, Star, ArrowRight, Shield, Check, ChevronDown, MapPin } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import BookingHeader from "../components/BookingHeader";
import BookingFooter from "../components/BookingFooter";
import "./Booking.css";

export default function Booking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to hash section on page load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [location]);

  // Refs for scroll animations
  const sectionTwoRef = useRef(null);
  const sectionFiveRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Manual scroll progress (replaces useScroll which can fail with certain DOM layouts)
  const sectionTwoScroll = useMotionValue(0);

  useEffect(() => {
    // IMPORTANT: window doesn't scroll — the .app div is the scroll container
    // (App.css sets html/body/root overflow:hidden, .app overflow-y:auto)
    const scrollContainer = document.querySelector(".app") || window;

    const handleScroll = () => {
      const el = sectionTwoRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const viewportHeight = (scrollContainer === window ? window.innerHeight : scrollContainer.clientHeight);
      const scrollableHeight = el.offsetHeight - viewportHeight + 80;

      if (scrollableHeight <= 0) return;

      // Progress starts when section top reaches the 80px sticky threshold
      const stickyThreshold = 80;
      const currentScroll = -rect.top + stickyThreshold;
      const progress = Math.max(0, Math.min(1, currentScroll / scrollableHeight));
      sectionTwoScroll.set(progress);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // run once on mount
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [sectionTwoScroll]);

  // Image animations
  const imageX = useTransform(sectionTwoScroll, [0.1, 0.8], ["0vw", "26vw"]);
  const imageScale = useTransform(sectionTwoScroll, [0.1, 0.7], [1.1, 0.95]);
  const imageRadius = useTransform(sectionTwoScroll, [0.1, 0.7], ["0px", "40px"]);
  const imageRotateY = useTransform(sectionTwoScroll, [0.1, 0.7], [0, -8]);
  const imageRotateX = useTransform(sectionTwoScroll, [0.1, 0.7], [0, 5]);
  const imageZ = useTransform(sectionTwoScroll, [0.1, 0.7], [0, 50]);
  const imageGrayscale = useTransform(sectionTwoScroll, [0, 0.3], ["100%", "0%"]);

  // Grayscale filter hook (Fix for Framer Motion reactive strings)
  const grayscaleFilter = useMotionTemplate`grayscale(${imageGrayscale})`;

  // Text animations
  const textOpacity = useTransform(sectionTwoScroll, [0.2, 0.6], [0, 1]);
  const textX = useTransform(sectionTwoScroll, [0.2, 0.8], ["-60vw", "-24vw"]);
  const bgTextY = useTransform(sectionTwoScroll, [0, 1], ["0%", "-30%"]);
  const bgTextOpacity = useTransform(sectionTwoScroll, [0.1, 0.4, 0.6, 0.9], [0, 0.08, 0.08, 0]);

  // FAQ State
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const faqs = [
    {
      q: "Can I attend if I am not in Mumbai?",
      a: "Yes! You can join the exclusive Global Webinar from anywhere."
    },
    {
      q: "What is the event about?",
      a: "A high-impact masterclass where international artist Kostya decodes the Architecture of Volume. You’ll learn 3 exclusive Volume techniques to master silhouettes and elevate your Skills."
    },
    {
      q: "Who should attend the event?",
      a: "Professional hairstylists and salon owners looking to master international standards. It’s for those who want to move beyond basic styling to high-end technical artistry."
    },
    {
      q: "What will I learn?",
      a: "You’ll master 3 signature volume techniques, advanced sectioning, and tool handling. The focus is on creating gravity-defying, long-lasting silhouettes for Indian hair."
    },
    {
      q: "Where is the event?",
      a: "The live seminar is in Mumbai (Taj Santacruz), or you can join via the Global Webinar. It’s designed to be accessible whether you are in the city or tuning in remotely."
    },
    {
      q: "What is the duration?",
      a: "A high-intensity 4-hour session from 3:00 PM to 7:00 PM. It includes a live demo, technical breakdown, and a networking session."
    },
    {
      q: "Will I get a certificate?",
      a: "Yes, all participants receive an official e-certificate signed by Kostya. It’s a prestigious credential to showcase your international training to clients."
    }
  ];

  return (
    <main className="booking-page-root relative bg-black text-white">
      <BookingHeader />

      {/* Hero Section */}
      <div id="hero" className="p-2 md:p-6 lg:p-10 lg:pb-0 min-h-screen pt-16 md:pt-20">
        <section className="relative min-h-[calc(100vh-2rem)] md:min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] w-full flex items-center justify-start px-6 md:px-16 lg:px-24 py-12 rounded-[2rem] overflow-hidden border border-white/5 shadow-2xl">
          {/* Triple Portrait Video Background */}
          <div className="absolute inset-0 z-0 bg-gray-900 flex">
            {[
              { id: 1, src: "/calyx_intro.mp4" },
              { id: 2, src: "/teaser_video.mp4" },
              { id: 3, src: "/demo_video.mp4" }
            ].map((video, idx) => (
              <div key={video.id} className={`flex-1 h-full relative overflow-hidden ${idx !== 2 ? 'border-r border-white/5' : ''}`}>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={`h-full w-full object-cover opacity-60 grayscale hover:grayscale-0 transition-all duration-1000 ${idx === 1 ? 'scale-110' : ''}`}
                >
                  <source src={video.src} type="video/mp4" />
                </video>
              </div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/20 to-black/80" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
          </div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 w-full max-w-7xl mx-auto">
            <motion.div
              initial={isMobile ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex flex-col justify-center space-y-4"
            >
              <div className="space-y-3">
                <motion.div
                  initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center px-3 py-1 rounded-full border border-gold-500/30 bg-gold-500/10 text-gold-400 text-[10px] font-bold uppercase tracking-[0.2em]"
                >
                  <Star className="w-3 h-3 mr-2 fill-gold-400" />
                  Exclusive Masterclass
                </motion.div>

                <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold leading-[1.1] display-font">
                  Learn the <br />
                  <span className="gold-text">Architecture of Volume</span>
                </h1>

                <p className="text-sm md:text-base text-gray-400 max-w-lg font-light leading-relaxed">
                  Join International Hair Artists Kostya for a high-precision masterclass designed to decode the geometry and technical secrets behind gravity-defying, long-lasting volumes.
                </p>
              </div>

              <motion.div
                id="tickets"
                initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-card p-5 md:p-6 rounded-[1.5rem] max-w-[420px] group hover:border-gold-500/40 transition-all duration-700 w-full"
              >
                <div className="mb-4 pb-4 border-b border-white/10">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-[0.2em] mb-1 font-bold">Physical Event</p>
                      <h3 className="text-xl font-bold tracking-tight text-white">Exclusive Seminar</h3>
                    </div>
                  </div>
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center text-xs text-gray-400 font-light">
                      <div className="w-3.5 h-3.5 rounded-full bg-gold-500/10 flex items-center justify-center mr-3 border border-gold-500/20">
                        <MapPin className="w-2 h-2 text-gold-400" />
                      </div>
                      <span>Location: Mumbai</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-400 font-light">
                      <div className="w-3.5 h-3.5 rounded-full bg-gold-500/10 flex items-center justify-center mr-3 border border-gold-500/20">
                        <Star className="w-2 h-2 text-gold-400" />
                      </div>
                      <span>Venue: Taj Santacruz, Mumbai</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                    <div className="border border-white/5 rounded-lg p-2 bg-white/[0.02]">
                      <p className="text-gray-400 mb-1">Platinum</p>
                      <p className="font-bold gold-text text-sm">₹9,999</p>
                    </div>
                    <div className="border border-white/5 rounded-lg p-2 bg-white/[0.02]">
                      <p className="text-gray-400 mb-1">Gold</p>
                      <p className="font-bold text-white text-sm">₹7,499</p>
                    </div>
                    <div className="border border-white/5 rounded-lg p-2 bg-white/[0.02]">
                      <p className="text-gray-400 mb-1">Silver</p>
                      <p className="font-bold text-gray-300 text-sm">₹4,999</p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-gray-500 text-[9px] uppercase tracking-[0.2em] mb-0.5 font-bold">Online Masterclass</p>
                      <h3 className="text-base font-bold tracking-tight text-white">Global Webinar</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-500 line-through">₹2999</p>
                      <p className="text-lg font-bold gold-text">₹999</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => user ? navigate("/dashboard") : navigate("/login")}
                  className="w-full py-3 rounded-xl gold-gradient text-black text-sm font-bold flex items-center justify-center group/btn relative overflow-hidden transition-all duration-300 active:scale-[0.98] shadow-lg shadow-gold-900/20"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500" />
                  <span className="relative z-10 flex items-center tracking-tight">
                    Reserve Your Spot <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </span>
                </button>
              </motion.div>
            </motion.div>

            <div className="hidden lg:flex flex-col justify-end pb-12 items-end space-y-8">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="text-right">
                <div className="h-0.5 w-12 bg-gold-500 ml-auto mb-6" />
                <p className="text-7xl font-black text-white/[0.03] uppercase select-none leading-none mb-2 tracking-tighter">Artistry</p>
                <p className="text-xl font-light text-gray-500 tracking-[0.4em] uppercase">Master the craft</p>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      {/* Second Section: Info Bar */}
      <section id="schedule" className="relative py-0 bg-black border-y border-white/5 overflow-hidden">
        <div className="max-w-full mx-auto grid grid-cols-1 md:grid-cols-4 gap-0">
          {[
            { num: "01", title: "Start Time", detail: "03:00 PM IST", icon: <div className="w-3 h-3 rounded-full bg-gold-500 shadow-[0_0_10px_rgba(212,175,55,0.5)]" /> },
            { num: "02", title: "Duration", detail: "4 Hours Live", icon: <Star className="w-6 h-6 text-gold-500" /> },
            { num: "03", title: "Location", detail: "Mumbai, India", icon: <Calendar className="w-6 h-6 text-gold-400/80" /> },
            { num: "04", title: "Date", detail: "18 May, 2026", icon: <Users className="w-6 h-6 text-gold-400/80" /> }
          ].map((item, i) => (
            <div key={i} className="group relative px-6 py-12 md:py-10 flex flex-col items-center justify-center border-r border-white/10 last:border-0 hover:bg-white/[0.02] transition-colors duration-500 overflow-hidden">
              {/* Massive Background Number - Gold */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
                <span style={{
                  fontSize: "clamp(90px, 13vw, 160px)",
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: "-0.05em",
                  background: "linear-gradient(135deg, #D4AF37 0%, #F9E29C 50%, #B8860B 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  opacity: 0.22,
                }}>
                  {item.num}
                </span>
              </div>

              {/* Main Content - centered */}
              <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                <div className="h-8 flex items-center justify-center" style={{ color: "#D4AF37" }}>
                  {item.icon}
                </div>
                <div className="space-y-1 text-center">
                  <h3 style={{
                    fontSize: "13px",
                    textTransform: "uppercase",
                    letterSpacing: "0.4em",
                    color: "#FFFFFF",
                    fontWeight: 800,
                    marginBottom: "6px",
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    background: "linear-gradient(135deg, #D4AF37 0%, #F9E29C 50%, #B8860B 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    fontWeight: 700,
                    fontSize: "1.05rem",
                  }}>
                    {item.detail}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Third Section: Parallax */}
      <section
        id="master-artist"
        ref={sectionTwoRef}
        style={{ position: "relative", height: "auto", minHeight: isMobile ? "auto" : "300vh", background: "#000" }}
      >
        {/* Sticky Pinned Container */}
        <div style={{
          perspective: "1200px",
          position: isMobile ? "relative" : "sticky",
          top: isMobile ? "60px" : "80px",
          minHeight: isMobile ? "auto" : "calc(100vh - 80px)",
          height: isMobile ? "auto" : "calc(100vh - 80px)",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: isMobile ? "visible" : "hidden",
          background: "#000",
          paddingBottom: isMobile ? "40px" : "0",
        }}>
          {/* Watermark Background Text */}
          <motion.div
            style={isMobile ? { position: "absolute", inset: 0, opacity: 0.04 } : {
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              y: bgTextY, opacity: bgTextOpacity,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <h2 style={{ fontSize: "20vw", fontWeight: 900, textTransform: "uppercase", color: "rgba(255,255,255,0.05)", whiteSpace: "nowrap", fontFamily: "var(--font-display)" }}>Master Artist</h2>
          </motion.div>

          {/* Content Wrapper - both text and image are absolute children of this container */}
          <div className="relative w-full h-full flex items-center justify-center">

            {/* Left Text - revealed by scroll */}
            {!isMobile && (
              <motion.div
                style={{
                  opacity: textOpacity,
                  x: textX,
                  position: "absolute",
                  zIndex: 10,
                  textAlign: "left"
                }}
              >
                <h2 style={{ fontSize: "clamp(40px, 6vw, 80px)", fontWeight: 700, fontFamily: "var(--font-display)", lineHeight: 1, marginBottom: "32px", color: "#fff" }}>
                  Master of <br />
                  <span className="gold-text animate-pulse">Volume</span>
                </h2>
                <p style={{ fontSize: "1.1rem", color: "#9ca3af", fontWeight: 300, lineHeight: 1.7, maxWidth: "450px", marginBottom: "40px" }}>
                  Konstantin Borchininov is a globally recognized artist celebrated for his iconic volume curls, open hair styling, and glamorous setting techniques.
                </p>
                <div style={{ display: "flex", gap: "32px" }}>
                  {[{ label: "Precision", val: "100%" }, { label: "Artistry", val: "Global" }].map((stat, i) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column" }}>
                      <span className="gold-text" style={{ fontSize: "1.8rem", fontWeight: 700 }}>{stat.val}</span>
                      <span style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.2em", color: "#6b7280", fontWeight: 700 }}>{stat.label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Image — absolutely centered, slides right */}
            {!isMobile && (
              <motion.div
                style={{
                  position: "absolute",
                  zIndex: 20,
                  width: "45vw",
                  height: "70vh", // Increased slightly for better portrait display
                  x: imageX,
                  scale: imageScale,
                  borderRadius: imageRadius,
                  rotateY: imageRotateY,
                  rotateX: imageRotateX,
                  z: imageZ,
                  filter: grayscaleFilter,
                  willChange: "transform",
                  overflow: "hidden",
                  boxShadow: "0 50px 100px -20px rgba(0,0,0,0.8)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <img src="/IMG_0217.JPG" alt="Calyx Artist" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 15%", display: "block" }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 40%)" }} />
              </motion.div>
            )}

            {/* Mobile layout */}
            {isMobile && (
              <div style={{ padding: "0px 24px", display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
                <img src="/IMG_0217.JPG" alt="Calyx Artist" style={{ width: "100%", borderRadius: "24px" }} />
                <div>
                  <h2 style={{ fontSize: "2rem", fontWeight: 700, fontFamily: "var(--font-display)", color: "#fff", marginBottom: "12px" }}>
                    Master of <br /><span className="gold-text">Volume</span>
                  </h2>
                  <p style={{ color: "#9ca3af", lineHeight: 1.6, fontSize: "0.9rem" }}>Konstantin Borchininov is a globally recognized artist celebrated for his iconic volume curls, open hair styling, and glamorous setting techniques.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* Why You Should Not Miss It */}
      <section id="features" ref={sectionFiveRef} className="relative py-24 px-6 md:px-24 bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20 text-center space-y-4">
            <span className="text-gold-500 font-bold uppercase tracking-[0.5em] text-[10px]">The Ultimate Experience</span>
            <h2 className="text-5xl md:text-7xl font-bold display-font">Why You Should Not Miss It</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { num: "01", title: "Live Demonstration", desc: "Witness the creation of 3 exclusive hair silhouette techniques live on stage. Kostya reveals the signature volume mastering.", icon: <Star />, isGold: true },
              { num: "02", title: "Technique Breakdown", desc: "An intensive step-by-step breakdown of professional setting and styling geometry. Decode elite volume science.", icon: <Shield /> },
              { num: "03", title: "Interactive Q&A", desc: "A moderated session where Kostya provides an expert perspective on key styling inquiries. Questions are addressed throughout the masterclass based on their relevance, maintaining a sophisticated flow of insights.", icon: <Users />, isGold: true },
              { num: "04", title: "Elite Networking", desc: "Connect with top industry professionals and leaders. Expand your circle within the niche Indian artistry community.", icon: <Calendar /> },
              { num: "05", title: "Official Certificate", desc: "Receive an official completion certificate signed by Kostya. Validate your expertise in Volume techniques.", icon: <Check />, isGold: true },
              { num: "06", title: "Register for the Masterclass", desc: "Secure your place in the most anticipated Hair Styling Masterclass & learn the architecture of volume live in Mumbai.", icon: <ArrowRight />, isCTA: true }
            ].map((box, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={box.isCTA ? () => (user ? navigate("/dashboard") : navigate("/login")) : undefined}
                className={`p-9 rounded-[2.2rem] transition-all duration-700 shadow-2xl flex flex-col min-h-[320px] ${
                  box.isCTA ? "cursor-pointer hover:scale-[1.02] active:scale-[0.98]" : ""
                } ${box.isGold ? "gold-gradient text-black" : "glass-card text-white hover:border-gold-500/40"}`}
              >
                <span className={`text-4xl font-light mb-4 ${box.isGold ? 'text-black/20' : 'text-white/20'}`}>{box.num}</span>
                <h3 className="text-2xl font-bold mb-4 display-font">{box.title}</h3>
                <p className={`text-base leading-relaxed mb-8 flex-1 ${box.isGold ? 'text-black/85' : 'text-gray-200'}`}>{box.desc}</p>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${box.isGold ? 'bg-black text-gold-500' : 'bg-white/5 text-gold-500 border border-white/10'}`}>
                  {React.cloneElement(box.icon, {
                    className: "w-5 h-5",
                    stroke: box.isGold ? "#D4AF37" : "currentColor"
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 px-6 md:px-24 bg-black border-t border-white/5">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <span className="text-gold-500 font-bold uppercase tracking-[0.5em] text-[10px]">Need to know</span>
            <h2 className="text-4xl md:text-6xl font-bold display-font">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`border rounded-2xl overflow-hidden transition-all ${openFaqIndex === idx ? 'bg-white/[0.04] border-gold-500/30' : 'bg-white/[0.01] border-white/5'}`}
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                  className="w-full text-left px-8 py-6 flex justify-between items-center group"
                >
                  <span className={`text-base font-bold ${openFaqIndex === idx ? 'gold-text' : 'text-gray-300'}`}>{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openFaqIndex === idx ? 'text-gold-400 rotate-180' : 'text-gray-500'}`} />
                </button>
                <AnimatePresence>
                  {openFaqIndex === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <div className="px-8 pb-8 pt-2 text-gray-400 font-light text-sm leading-relaxed">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <BookingFooter />
    </main>
  );
}
